import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import Input, { MaskTypes } from '~/components/input';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import TableList from '~/components/tableList';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { FormField, setFormState } from '~/utils/form';
import { cpfMask, creditCardMask } from '~/utils/masks';
import { formatMoney } from '../shared/format';
import { IListColCampaing } from '~/views/Resource/Campaing/model';

import * as St from './styles';

export interface DebtNegotiationClientState {
  cpf: string;
  nome: string;
  numeroCartao: string;
  statusCartao: string;
  limiteCreditoUtilizado?: number;
  limiteEmprestimoUtilizado?: number;
  limiteExtraUtilizado?: number;
  limiteTotalUtilizado?: number;
  limiteTotal?: number;
  limiteCompras?: number;
  limiteEmprestimo?: number;
  limite12x?: number;
  scoreInadimplencia?: {
    dataAtualizacao?: string;
    risco?: string;
    score?: number;
  };
  eligibleDetail?: Record<string, unknown>;
}

interface Form {
  cpf: FormField;
}

const INITIAL_FORM: Form = {
  cpf: { name: 'cpf', value: '' },
};

const customerHeader = [
  {
    id: 1,
    value: 'Nome completo',
    maxChar: 28,
    textAlign: 'left',
  },
  {
    id: 2,
    value: 'CPF',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 3,
    value: 'Cartão',
    maxChar: 22,
    textAlign: 'center',
  },
  {
    id: 4,
    value: 'Status',
    maxChar: 22,
    textAlign: 'center',
  },
  {
    id: 5,
    value: 'Score alto risco',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 6,
    value: 'Limite total',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 7,
    value: 'Limite de compras',
    maxChar: 18,
    textAlign: 'center',
  },
  {
    id: 8,
    value: 'Limite empréstimo',
    maxChar: 20,
    textAlign: 'center',
  },
  {
    id: 9,
    value: 'Limite 12x',
    maxChar: 14,
    textAlign: 'center',
  },
];

const TAG_PALETTE = {
  red: { color: '#e71037', background: '#fdecee' },
  orange: { color: '#f5a623', background: '#fff6e5' },
  green: { color: '#1eab35', background: '#e8f7ec' },
};

const STATUS_TAGS: Record<
  string,
  { label: string; tone: keyof typeof TAG_PALETTE }
> = {
  ATIVO: { label: 'Ativo', tone: 'green' },
  INATIVO: { label: 'Inativo', tone: 'red' },
  INADIMPLENTE: { label: 'Inadimplente', tone: 'red' },
};

const scoreTone = (score?: number): keyof typeof TAG_PALETTE | null => {
  if (score === undefined || score === null) return null;
  if (score >= 700) return 'red';
  if (score >= 300) return 'orange';
  return 'green';
};

const renderTag = (label: string, tone: keyof typeof TAG_PALETTE) => {
  const palette = TAG_PALETTE[tone];

  return (
    <St.OvalTag $color={palette.color} $background={palette.background}>
      <St.RiskDot $color={palette.color} />
      {label}
    </St.OvalTag>
  );
};

const renderStatus = (status?: string) => {
  const tag = STATUS_TAGS[String(status || '').toUpperCase()];

  if (!tag) return status || '-';

  return renderTag(tag.label, tag.tone);
};

const renderScore = (score?: number) => {
  const tone = scoreTone(score);

  if (!tone) return '-';

  return renderTag(String(score), tone);
};

const toClientState = (
  client: DebtNegotiationClientState,
): DebtNegotiationClientState => ({
  ...client,
  limiteTotal: client.limiteTotalUtilizado,
  limiteCompras: client.limiteCreditoUtilizado,
  limiteEmprestimo: client.limiteEmprestimoUtilizado,
  limite12x: client.limiteExtraUtilizado,
});

const normalizeClients = (payload: unknown): DebtNegotiationClientState[] => {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    return payload.map(toClientState);
  }

  if (typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return record.data.map((item) =>
      toClientState(item as DebtNegotiationClientState),
    );
  }

  if (record.data && typeof record.data === 'object') {
    return [toClientState(record.data as DebtNegotiationClientState)];
  }

  if ('cpf' in record || 'numeroCartao' in record) {
    return [toClientState(record as unknown as DebtNegotiationClientState)];
  }

  return [];
};

const PAGE_SIZE = 10;

const readClientPage = (payload: unknown) => {
  const clients = normalizeClients(payload);
  const record =
    payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : null;
  const reportedPages = Number(record?.totalPages);
  let totalPages = clients.length ? 1 : 0;

  if (Number.isFinite(reportedPages) && reportedPages > 0) {
    totalPages = reportedPages;
  }

  return { clients, totalPages };
};

const readClientRecord = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;

  if ('elegivelNegociacao' in record || 'cpf' in record) return record;

  if (record.data && typeof record.data === 'object') {
    return record.data as Record<string, unknown>;
  }

  return null;
};

const ineligibleMessage = (reason: string) => {
  if (reason === 'NAO_ELEGIVEL_AUTORIZADOR') {
    return (
      <>
        <p>Não é possível simular acordo para este cliente.</p>
        <p>O status do cartão não permite negociação.</p>
        <p>
          A geração de acordo está bloqueada para cartões com status
          {' '}
          <strong>Cobrança</strong>
          {' '}
          ou
          {' '}
          <strong>Cancelado Exp./Solic.</strong>
        </p>
      </>
    );
  }

  if (reason === 'NAO_ELEGIVEL_SCORE') {
    return (
      <>
        <p>Não é possível simular acordo para este cliente.</p>
        <p>O score de risco está abaixo do mínimo exigido.</p>
        <p>
          A geração de acordo está bloqueada para clientes com score de risco
          abaixo de
          {' '}
          <strong>300</strong>
          {' '}
          <strong>pts</strong>
          .
        </p>
      </>
    );
  }

  if (reason === 'NEGOCIACAO_EM_ANDAMENTO') {
    return (
      <p>
        Não é possível simular acordo para esse cliente, cliente já possui
        {' '}
        <strong>negociação em aberto</strong>
      </p>
    );
  }

  return <p>Não é possível simular acordo para este cliente.</p>;
};

const DebtNegotiationClientSearch = (): JSX.Element => {
  const history = useHistory();
  const { user } = useAuth();
  const checkingEligibility = useRef(false);
  const [form, setForm] = useState<Form>(INITIAL_FORM);
  const [list, setList] = useState<IListColCampaing[][]>([]);
  const [pageClients, setPageClients] = useState<DebtNegotiationClientState[]>(
    [],
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [ineligibleOpen, setIneligibleOpen] = useState(false);
  const [ineligibleReason, setIneligibleReason] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [searchKey, setSearchKey] = useState(0);
  const [searchedCpf, setSearchedCpf] = useState('');
  const searchEligibleClient = useApi<DebtNegotiationClientState>(
    Endpoints.debtNegotiation.listClients,
    ApiMethod.POST,
  );
  const checkEligibleClient = useApi(
    Endpoints.debtNegotiation.getEligibleClient,
    ApiMethod.POST,
  );
  const selectedClient =
    selectedIndex === null ? null : pageClients[selectedIndex] || null;

  const cpfDigits = form.cpf?.unmasked || '';
  const cpfIsPartial = cpfDigits.length > 0 && cpfDigits.length !== 11;

  const buildRow = (client: DebtNegotiationClientState): IListColCampaing[] => [
    {
      border: true,
      title: 'Nome completo',
      value: client.nome,
      maxChar: 28,
      textAlign: 'left',
    },
    {
      border: true,
      title: 'CPF',
      value: cpfMask(client.cpf).masked,
      maxChar: 16,
      textAlign: 'center',
    },
    {
      title: 'Cartão',
      value: creditCardMask(client.numeroCartao).masked,
      maxChar: 22,
      textAlign: 'center',
    },
    {
      title: 'Status',
      value: renderStatus(client.statusCartao),
      maxChar: 22,
      textAlign: 'center',
    },
    {
      title: 'Score alto risco',
      value: renderScore(client.scoreInadimplencia?.score),
      maxChar: 16,
      textAlign: 'center',
    },
    {
      title: 'Limite total',
      value: formatMoney(client.limiteTotalUtilizado),
      maxChar: 16,
      textAlign: 'center',
    },
    {
      title: 'Limite de compras',
      value: formatMoney(client.limiteCreditoUtilizado),
      maxChar: 18,
      textAlign: 'center',
    },
    {
      title: 'Limite empréstimo',
      value: formatMoney(client.limiteEmprestimoUtilizado),
      maxChar: 20,
      textAlign: 'center',
    },
    {
      title: 'Limite 12x',
      value: formatMoney(client.limiteExtraUtilizado),
      maxChar: 14,
      textAlign: 'center',
    },
  ];

  const fetchClients = async (page: number, cpf = searchedCpf) => {
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

    if (!cpfOperador) {
      setFeedback('Não foi possível identificar o operador.');
      return;
    }

    if ((cpf && cpf.length !== 11) || searchEligibleClient.loading) return;

    setFeedback('');

    try {
      const response = await searchEligibleClient.callApi(
        cpf
          ? { cpfOperador, cpf }
          : { cpfOperador, page, size: PAGE_SIZE },
      );

      if (!(response.status >= 200 && response.status < 300)) {
        setList([]);
        setPageClients([]);
        setSelectedIndex(null);
        setTotalPages(0);
        setFeedback(
          response.mensagem || 'Não foi possível consultar os clientes.',
        );
        return;
      }

      const { clients, totalPages: pages } = readClientPage(response.data);

      if (!clients.length) {
        setList([]);
        setPageClients([]);
        setSelectedIndex(null);
        setTotalPages(0);
        setFeedback('Nenhum cliente encontrado.');
        return;
      }

      setTotalPages(pages);
      setPageClients(clients);
      setSelectedIndex(null);
      setList(clients.map((client) => buildRow(client)));
    } catch (error) {
      setList([]);
      setPageClients([]);
      setSelectedIndex(null);
      setTotalPages(0);
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar os clientes.',
      );
    }
  };

  useEffect(() => {
    fetchClients(1, '');
  }, [user?.cpf]);

  const handleSearch = () => {
    setSearchedCpf(cpfDigits);
    setSearchKey((current) => current + 1);
    fetchClients(1, cpfDigits);
  };

  const simulateAgreement = async () => {
    if (!selectedClient || checkingEligibility.current) return;

    const cpf = String(selectedClient.cpf || '').replace(/\D/g, '');
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

    if (!cpf || !cpfOperador) {
      setFeedback('Não foi possível identificar o cliente ou o operador.');
      return;
    }

    checkingEligibility.current = true;
    setFeedback('');

    try {
      const response = await checkEligibleClient.callApi({
        cpf,
        cpfOperador,
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setFeedback(
          response.mensagem ||
            'Não foi possível consultar a elegibilidade do cliente.',
        );
        return;
      }

      const clientRecord = readClientRecord(response.data);
      const eligible =
        clientRecord && 'elegivelNegociacao' in clientRecord
          ? Boolean(clientRecord.elegivelNegociacao)
          : undefined;

      if (eligible === false) {
        setIneligibleReason(String(clientRecord?.motivoInelegibilidade || ''));
        setIneligibleOpen(true);
        return;
      }

      if (eligible !== true) {
        setFeedback('Não foi possível confirmar a elegibilidade do cliente.');
        return;
      }

      history.push('/negociacao-dividas/cliente', {
        ...selectedClient,
        eligibleDetail: clientRecord,
      });
    } catch (error) {
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar a elegibilidade do cliente.',
      );
    } finally {
      checkingEligibility.current = false;
    }
  };

  const handleClear = () => {
    setForm({
      cpf: { name: 'cpf', value: '', unmasked: '' },
    });
    setSearchedCpf('');
    setSearchKey((current) => current + 1);
    fetchClients(1, '');
  };

  return (
    <Container>
      <Dialog
        icon={icons.exclamation}
        open={ineligibleOpen}
        positiveLabel="Entendi"
        positiveAction={() => setIneligibleOpen(false)}
        widthInPixel={460}
      >
        <St.IneligibleMessage>
          {ineligibleMessage(ineligibleReason)}
        </St.IneligibleMessage>
      </Dialog>
      <St.BoxCard>
        <ContainerFlex row space>
          <ContainerFlex padding={16}>
            <Input
              label="CPF"
              name={form.cpf.name}
              value={form.cpf.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="000.000.000-00"
              maxLength={14}
              mask={MaskTypes.CPF}
            />
          </ContainerFlex>
          <ContainerFlex padding={16} row gap="12" align="flex-end">
            <Button fitContent outlined onClick={handleClear}>
              <span>Limpar pesquisa</span>
            </Button>
            <Button
              fitContent
              onClick={handleSearch}
              disabled={cpfIsPartial || searchEligibleClient.loading}
              loading={searchEligibleClient.loading}
            >
              <span>Buscar</span>
            </Button>
          </ContainerFlex>
        </ContainerFlex>
        {feedback && <St.Feedback>{feedback}</St.Feedback>}
      </St.BoxCard>

      <St.RiskLegend>
        <St.RiskCopy>
          <St.RiskTitle>Clientes com alto risco de inadimplência</St.RiskTitle>
          <St.RiskSubtitle>
            Ordenados do maior score (1.000) para o menor (300)
          </St.RiskSubtitle>
        </St.RiskCopy>
        <St.RiskItems>
          <St.RiskItem $color="#e71037">
            <St.RiskDot $color="#e71037" />
            Crítico (700-1000)
          </St.RiskItem>
          <St.RiskSeparator />
          <St.RiskItem $color="#f5a623">
            <St.RiskDot $color="#f5a623" />
            Alto (300-699)
          </St.RiskItem>
          <St.RiskSeparator />
          <St.RiskItem $color="#1eab35">
            <St.RiskDot $color="#1eab35" />
            Baixo (&lt;300)
          </St.RiskItem>
        </St.RiskItems>
      </St.RiskLegend>

      <Loading
        status={searchEligibleClient.loading || checkEligibleClient.loading}
      />

      {list.length > 0 && (
        <St.BoxCard>
          <TableList
            customerHeader={customerHeader}
            list={list}
            selectedRowIndex={selectedIndex}
            onRowClick={setSelectedIndex}
          />
          <St.TableActions>
            <Pagination
              key={searchKey}
              totalPages={totalPages}
              onCurrentPageChange={fetchClients}
            />
            <div>
              {selectedClient && (
                <St.SelectedBlock>
                  <St.SelectedText>
                    <St.SelectedCount>
                      <St.RiskDot $color="#e71037" />
                      1 cliente selecionado
                    </St.SelectedCount>
                    <St.SelectedName>{selectedClient.nome}</St.SelectedName>
                  </St.SelectedText>
                  <Button
                    fitContent
                    onClick={simulateAgreement}
                    loading={checkEligibleClient.loading}
                    disabled={checkEligibleClient.loading}
                  >
                    <span>Simular acordo</span>
                  </Button>
                </St.SelectedBlock>
              )}
            </div>
          </St.TableActions>
        </St.BoxCard>
      )}
    </Container>
  );
};

export default DebtNegotiationClientSearch;
